// Sprint-005 C4 — Animals + Projects ("More" tab) copy helpers. Same
// governing rule as design-spec-today-card.md §4 / `lib/adminCopy.ts`:
// translate concepts, never surface internal/technical terms — here,
// specifically never "health_event," "record," "enum," or "bid" to any
// member/manager screen. Centralized so the same phrase is never spelled
// two different ways across `AnimalDetailOverlay`/`AddHealthNoteSheet`/
// `ProjectDetailOverlay`.

import type { HealthEventTypeValue, PunchListItem } from "../types";

/** Plain-language labels for `HealthEventType` (mirrors
 * `app/models/enums.py::HealthEventType`) — hardcoded client-side, same
 * established precedent as `adminCopy.ts::SCHEDULE_TYPE_OPTIONS`. These are
 * farm vocabulary (dewormer, farrier), not systemy jargon, so they're shown
 * plainly rather than translated further — only the raw enum *value* (a
 * lowercase code, never surfaced) is what the zero-jargon rule guards
 * against. */
export const HEALTH_EVENT_TYPE_OPTIONS: Array<{ value: HealthEventTypeValue; label: string }> = [
  { value: "dewormer", label: "Dewormer" },
  { value: "vaccine", label: "Vaccine" },
  { value: "farrier", label: "Farrier" },
  { value: "dental", label: "Dental" },
  { value: "vet", label: "Vet visit" },
  { value: "other", label: "Other" },
];

export function healthEventTypeLabel(type: string): string {
  return HEALTH_EVENT_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? "Note";
}

/** "2026-06-03" -> "June 3" — plain-language day, no year (matching the
 * design brief's own example: "Trimmed hooves — June 3"). A bare
 * `event_date` (no time-of-day) is parsed as local, not UTC-shifted —
 * appending `T00:00:00` avoids the classic off-by-one-day bug the same way
 * `app.tsx`'s own `DATE_FORMATTER` usage does for `data.date`. */
const MONTH_DAY_FORMATTER = new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric" });

export function monthDayLabel(isoDate: string): string {
  return MONTH_DAY_FORMATTER.format(new Date(`${isoDate}T00:00:00`));
}

/** The Projects detail screen's read-only checklist heading — deliberately
 * not "Punch list" (a trade term the admin never uses in the app's own voice)
 * and not the DB column name. */
export const PROJECT_CHECKLIST_HEADING = "Still to do";

export function projectChecklistItemLabel(item: PunchListItem): string {
  return item.item;
}

/** The bids resource's plain-language label everywhere it appears in the
 * UI (admin-only — see `hooks/useProjectBids.ts`). Never "bids,"
 * "quotes/estimates" interchangeably, never "financials." */
export const PROJECT_QUOTES_HEADING = "Quotes";

export const ANIMALS_EMPTY_HINT = "No animals here yet.";
export const PROJECTS_EMPTY_HINT = "No projects here yet.";
export const HEALTH_TIMELINE_EMPTY_HINT = "Nothing logged yet.";
/** Sprint-011 A8 — the `.animal-card-note-badge` label, shared by
 * Dashboard's Animals-snapshot mini-grid and the full Animals-page grid
 * (both via `hooks/useAnimalHealthNotes.ts`'s 14-day window). */
export const HEALTH_NOTE_BADGE_LABEL = "Health note";
/** Neutral chip for a recent event that is routine husbandry (dewormer,
 * vaccine, farrier, dental, or an explicit all-clear): still visible, but
 * never the clay alarm. See `lib/healthNotes.ts`. */
export const ROUTINE_CARE_BADGE_LABEL = "Routine care";

/** design-spec-animal-cards-2026-07-10.md §2 (Option B) inactive-state
 * pill — the pill-shaped sibling of `.admin-tag.is-paused`'s "Taking a
 * break" text, but for animals specifically "Inactive" (never "Archived"/
 * "Deactivated" — matches the archive-not-delete verb used everywhere
 * else in this app). Wired to the shared `AnimalCard` component's
 * `inactive` prop, but per the spec's own handoff checklist, no caller
 * derives this from real `animal.active === false` data yet — UX gap #8
 * (whether inactive animals are even in-scope for this grid) is still an
 * open PM/product decision. */
export const ANIMAL_INACTIVE_TAG_LABEL = "Inactive";

/**
 * design-spec-animal-cards-2026-07-10.md §0 "`aria-label` on the card" —
 * the exact composed accessible name for every animal-card option,
 * shared so `AnimalsView`/`DashboardView`'s two grids (and any future
 * caller) never spell it two different ways:
 *   - Base: `"{name}, {species}"`, or `"{name}, species unconfirmed"`
 *     when `species` is null.
 *   - `", recent health note"` appended when `hasHealthNote`.
 *   - `", inactive"` appended when `inactive`.
 * Species/health-note/inactive state must never be conveyed by color
 * alone — this composed label plus the card's own visible text/chips is
 * what satisfies that rule. Matches the spec's own worked example
 * verbatim: `composeAnimalAriaLabel({ name: "Biscuit", species: "horse" },
 * { hasHealthNote: true, inactive: false })` -> `"Biscuit, horse, recent
 * health note"`.
 */
export function composeAnimalAriaLabel(
  animal: { name: string; species: string | null },
  state: { hasHealthNote: boolean; inactive: boolean; hasRoutineCare?: boolean },
): string {
  const base = animal.species
    ? `${animal.name}, ${animal.species}`
    : `${animal.name}, species unconfirmed`;
  const parts = [base];
  if (state.hasHealthNote) parts.push("recent health note");
  else if (state.hasRoutineCare) parts.push("recent routine care");
  if (state.inactive) parts.push("inactive");
  return parts.join(", ");
}

// ---------------------------------------------------------------------------
// Sprint-013 C1 (design-spec-animal-cards-2026-07-10.md §5, ux-ruling-
// inactive-grid-2026-07-10.md — E1 PM ruling) — the Animals Add/Edit
// form-sheet, the Active/All toggle, and Archive. Resolves TD-023: the
// `AnimalCard`/`AnimalsView` `inactive` prop is wired to real
// `animal.active === false` data for the first time this sprint.
// ---------------------------------------------------------------------------

/** §5's Add-sheet species field (CD blocking change #3) — an editable
 * combobox (`<input list=…>` + `<datalist>`), never a closed `<select>`.
 * This list is illustrative suggestions only, per the spec's own
 * "vocabulary to be confirmed with Backend Developer" caveat — any string
 * is a valid submission, including one not in this list. */
export const ANIMAL_SPECIES_SUGGESTIONS: string[] = [
  "Goat",
  "Pig",
  "Chicken",
  "Dog",
  "Horse",
  "Cat",
];

/** The full datalist options for the species combobox: the illustrative
 * suggestions above, UNION every distinct `species` value already present
 * in the fetched herd (so a real, already-in-use freeform value like
 * "likely horse" shows up as a suggestion too — never just the six
 * mockup placeholders). Deduplicated case-insensitively (first-seen
 * casing wins), sorted alphabetically. */
export function speciesDatalistOptions(animals: Array<{ species: string | null }>): string[] {
  const seen = new Map<string, string>();
  for (const value of ANIMAL_SPECIES_SUGGESTIONS) {
    seen.set(value.toLowerCase(), value);
  }
  for (const animal of animals) {
    const trimmed = animal.species?.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (!seen.has(key)) seen.set(key, trimmed);
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

/** §5's Add entry point — the `AnimalsView` header's `"+ Add"` button. */
export const ADD_ANIMAL_BUTTON_LABEL = "+ Add";
export const ADD_ANIMAL_SHEET_TITLE = "Add an animal";

export function editAnimalSheetTitle(animalName: string): string {
  return `Edit ${animalName}`;
}

export const ANIMAL_NAME_FIELD_LABEL = "Name";
export const ANIMAL_SPECIES_FIELD_LABEL = "Species (optional)";
export const ANIMAL_BIRTH_DATE_FIELD_LABEL = "Birth date (optional)";
export const ANIMAL_PADDOCK_FIELD_LABEL = "Paddock (optional)";
/** Sprint-016 F1 AN-01 (ui-redesign-direction-2026-07-11.md item 1, CD
 * binding condition 1) — `notes` is now a field on this same sheet,
 * `mode="edit"` only, appended LAST after the structural fields, always
 * carrying this visible label. Plain "Notes" (never "(optional)") —
 * matches the retired inline textarea's own label text verbatim, since
 * nothing about the field itself changed, only where it's edited. */
export const ANIMAL_NOTES_FIELD_LABEL = "Notes";

/** §5's 409 name-conflict handling — the specific required message, never
 * the generic `COULD_NOT_SAVE_ERROR` fallback for this one case. */
export function animalNameConflictMessage(name: string): string {
  return `An animal named '${name}' already exists.`;
}

// ---------------------------------------------------------------------------
// Sprint-013 C2 (design-spec-animal-cards-2026-07-10.md §5, mirrored from
// C1's Animals CRUD copy above; sa-ruling-crud-endpoints-2026-07-10.md
// Decision 2) — the Projects Add/Edit form-sheet, the Active/All toggle,
// Archive, and Restore. §5 is explicit this pattern is binding for all
// three entities, not just animals.
// ---------------------------------------------------------------------------

/** §5's Add entry point — the `ProjectsView` header's `"+ Add"` button.
 * Deliberately a separate constant from `ADD_ANIMAL_BUTTON_LABEL` (same
 * literal value) — each entity's copy is named/owned independently, same
 * convention already established across this file. */
export const ADD_PROJECT_BUTTON_LABEL = "+ Add";
export const ADD_PROJECT_SHEET_TITLE = "Add a project";

export const PROJECT_NAME_FIELD_LABEL = "Name";
export const PROJECT_STATUS_FIELD_LABEL = "Status";
export const PROJECT_SUMMARY_FIELD_LABEL = "Summary (optional)";

/** §5's 409 name-conflict handling — the specific required message,
 * mirroring `animalNameConflictMessage` for the Projects entity (projects
 * share the same unique-name-index posture as animals, per the UX doc
 * §2.2). */
export function projectNameConflictMessage(name: string): string {
  return `A project named '${name}' already exists.`;
}

// ---------------------------------------------------------------------------
// ux-ruling-inactive-grid-2026-07-10.md — E1 PM ruling precedent, mirrored
// for Projects (sprint-013 C2). The two-state "Active" (default) / "All"
// toggle in `ProjectsView`'s header, and the two new empty-state copy
// strings it introduces — "Active" here means "not archived," the same
// meaning `archived_at == null` carries server-side.
// ---------------------------------------------------------------------------

export const PROJECTS_FILTER_TOGGLE_LABEL = "Show projects";
export const PROJECTS_FILTER_ACTIVE_LABEL = "Active";
export const PROJECTS_FILTER_ALL_LABEL = "All";
export const PROJECTS_SHOW_ALL_LABEL = "Show all";

/** Mirrors `animalsListCountAnnouncement` — a live region announcing the
 * rendered count, refreshed whenever the toggle changes. */
export function projectsListCountAnnouncement(count: number): string {
  return `Projects list, ${count} item${count === 1 ? "" : "s"}`;
}

/** Mirrors `noActiveAnimalsHint` — the "zero active projects, but archived
 * ones exist" empty state. The bracketed "[Show all]" text is a real
 * tappable control (rendered separately by the caller), never a dead-end
 * sentence. */
export function noActiveProjectsHint(archivedCount: number): string {
  return `No active projects right now. You have ${archivedCount} archived —`;
}

/** design-spec-animal-cards-2026-07-10.md §0 "Inactive-animal treatment,"
 * mirrored for a project's archived state — the rectangular sibling of
 * `.admin-tag.is-paused`'s "Taking a break" text, matching the Admin list
 * row family's own tag shape (Option C, which `ProjectsView` already
 * uses) rather than Option B's pill (which Animals uses). Never "Deleted"/
 * "Removed" — matches the archive-not-delete verb used everywhere else in
 * this app. */
export const PROJECT_ARCHIVED_TAG_LABEL = "Archived";

// ---------------------------------------------------------------------------
// design-spec-animal-cards-2026-07-10.md §5 — Archive (admin/api only,
// `ConfirmDialog` with `destructive`), mirrored for Projects. Copy adapted
// from the spec's own "Recommended copy" for animals — same reassurance
// register (reversible, nothing deleted), reworded for a project's own
// fields (status/notes/checklist instead of health history/photos).
// ---------------------------------------------------------------------------

export function archiveProjectConfirmTitle(projectName: string): string {
  return `Archive ${projectName}?`;
}

export const ARCHIVE_PROJECT_CONFIRM_BODY =
  "It'll be moved out of the default Projects list. Its status, notes, and to-do checklist stay " +
  "on record, and you can restore it any time — nothing is deleted.";

export const ARCHIVE_PROJECT_CONFIRM_LABEL = "Archive";
export const ARCHIVE_PROJECT_BUTTON_LABEL = "Archive";

export function projectArchivedMessage(projectName: string): string {
  return `${projectName} archived.`;
}

// ---------------------------------------------------------------------------
// sa-ruling-crud-endpoints-2026-07-10.md Decision 2.4 — Restore (admin/api
// only). Projects get a dedicated restore endpoint (animals do not — they
// reuse `PATCH .../active`), so this is new ground, not a mirror of an
// existing animals affordance. Restoring is reversible/non-destructive
// (the archive it undoes was itself already confirmed), so it deliberately
// does NOT go through `ConfirmDialog` — matching this app's own "no
// confirmation modals for reversible actions" rule (`ConfirmDialog.tsx`'s
// docstring).
// ---------------------------------------------------------------------------

export const RESTORE_PROJECT_BUTTON_LABEL = "Restore";

export function projectRestoredMessage(projectName: string): string {
  return `${projectName} restored.`;
}

// ---------------------------------------------------------------------------
// ux-ruling-inactive-grid-2026-07-10.md — E1 PM ruling (BINDING). The
// two-state "Active" (default) / "All" toggle in `AnimalsView`'s header,
// and the two new empty-state copy strings it introduces.
// ---------------------------------------------------------------------------

export const ANIMALS_FILTER_TOGGLE_LABEL = "Show animals";
export const ANIMALS_FILTER_ACTIVE_LABEL = "Active";
export const ANIMALS_FILTER_ALL_LABEL = "All";
export const ANIMALS_SHOW_ALL_LABEL = "Show all";

/** E1 ruling's "Accessibility notes — List count announcement" — a live
 * region announcing the rendered count, refreshed whenever the toggle
 * changes, matching the app's own established "list, N items" convention
 * (`styles.css` ~line 108) rather than leaving a stale pre-toggle count. */
export function animalsListCountAnnouncement(count: number): string {
  return `Animals list, ${count} item${count === 1 ? "" : "s"}`;
}

/** E1 ruling's new "zero active animals, but inactive ones exist" empty
 * state — the bracketed "[Show all]" text is a real tappable control
 * (rendered separately by the caller), never a dead-end sentence. */
export function noActiveAnimalsHint(inactiveCount: number): string {
  return `No active animals right now. You have ${inactiveCount} inactive —`;
}

// ---------------------------------------------------------------------------
// design-spec-animal-cards-2026-07-10.md §5 — Archive (admin/api only,
// `ConfirmDialog` with `destructive`). Copy is the spec's own literal
// "Recommended copy (shown in mockup)," stated there as deliberate, with
// one change: the mockup's "she/her" assumed every animal is female, so
// the body now uses neutral "it/its" (matching the Projects/Contacts
// archive bodies) and points at the "All" filter, where archived animals
// actually show up, instead of a non-existent "Inactive" list.
// ---------------------------------------------------------------------------

export function archiveAnimalConfirmTitle(animalName: string): string {
  return `Archive ${animalName}?`;
}

export const ARCHIVE_ANIMAL_CONFIRM_BODY =
  "It'll be moved out of the active Animals list. Its health history and photos stay on " +
  "record, and you can find it under All and restore it any time — nothing is deleted.";

export const ARCHIVE_ANIMAL_CONFIRM_LABEL = "Archive";
export const ARCHIVE_ANIMAL_BUTTON_LABEL = "Archive";

export function animalArchivedMessage(animalName: string): string {
  return `${animalName} archived.`;
}

// ---------------------------------------------------------------------------
// Sprint-016 F1 AN-02 (ux-audit-whole-app-2026-07-11.md — S1, PM fix-batch
// #1) — Restore (admin/api only), mirroring `RESTORE_PROJECT_BUTTON_LABEL`/
// `RESTORE_CONTACT_BUTTON_LABEL`'s Decision 2.4/3.4 precedent. Animals have
// no dedicated restore endpoint — `PATCH /animals/{id}` with `active: true`
// is the same endpoint `archiveAnimal` already uses the other direction
// (confirmed accepted server-side, `app/api/v1/animals.py`'s `PATCH`
// handler applies `active` from either boolean with no directional
// restriction). Restoring is reversible/non-destructive (the archive it
// undoes was itself already confirmed), so it deliberately does NOT go
// through `ConfirmDialog` — matching this app's own "no confirmation
// modals for reversible actions" rule (`ConfirmDialog.tsx`'s docstring).
// ---------------------------------------------------------------------------

export const RESTORE_ANIMAL_BUTTON_LABEL = "Restore";

export function animalRestoredMessage(animalName: string): string {
  return `${animalName} restored.`;
}

/** The Overview tab's read-only birth-date/paddock display (scope item 5
 * — no dedicated mockup exists for this; formatted plainly, in the same
 * `.admin-row-sub` slot species already occupies, visible to every role
 * that can already view the record — same posture as species itself). A
 * full month/day/YEAR format (unlike `monthDayLabel` above, which
 * deliberately omits the year for near-term event dates) since a birth
 * date can be many years in the past. */
const BIRTH_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "long",
  day: "numeric",
});

export function birthDateLabel(isoDate: string): string {
  return `Born ${BIRTH_DATE_FORMATTER.format(new Date(`${isoDate}T00:00:00`))}`;
}

export function paddockLabel(paddock: string): string {
  return `Paddock: ${paddock}`;
}

// design-phase-c.md §4/§6 — Contacts page. "Contacts," never "contractors"/
// "vendors" (§10's zero-jargon table).
export const CONTACTS_PAGE_SUBHEADING = "Who to call for what.";
export const CONTACTS_EMPTY_HINT = "No contacts yet.";

// ---------------------------------------------------------------------------
// Sprint-013 C3 (design-spec-animal-cards-2026-07-10.md §5, mirrored from
// C2's Projects CRUD copy above; sa-ruling-crud-endpoints-2026-07-10.md
// Decision 3) — the Contacts Add/Edit form-sheet, the Active/All toggle,
// Archive, and Restore. §5 is explicit this pattern is binding for all
// three entities, not just animals.
// ---------------------------------------------------------------------------

/** §5's Add entry point — the `ContactsView` header's `"+ Add"` button.
 * Deliberately a separate constant from `ADD_PROJECT_BUTTON_LABEL`/
 * `ADD_ANIMAL_BUTTON_LABEL` (same literal value) — each entity's copy is
 * named/owned independently, same convention already established across
 * this file. */
export const ADD_CONTACT_BUTTON_LABEL = "+ Add";
export const ADD_CONTACT_SHEET_TITLE = "Add a contact";

export const CONTACT_NAME_FIELD_LABEL = "Name";
/** `contact.contact` — genuinely freeform phone/email/etc.
 * (`app/models/contractors.py::Contractor.contact`, `String(300)`), so
 * "Phone or email" here is an honest description, not the misleading
 * catch-all `AddPersonSheet.tsx` deliberately moved away from (that field
 * silently only ever stored an email server-side — see
 * `add-person.test.tsx`'s own docstring on that bug). */
export const CONTACT_PHONE_OR_EMAIL_FIELD_LABEL = "Phone or email (optional)";
export const CONTACT_NOTES_FIELD_LABEL = "Notes (optional)";

export function editContactSheetTitle(contactName: string): string {
  return `Edit ${contactName}`;
}

/** §5's 409 name-conflict handling — the specific required message,
 * mirroring `projectNameConflictMessage`/`animalNameConflictMessage` for
 * the Contacts entity (contacts share the same unique-name-index posture,
 * `uq_contractors_name`). */
export function contactNameConflictMessage(name: string): string {
  return `A contact named '${name}' already exists.`;
}

// ---------------------------------------------------------------------------
// ux-ruling-inactive-grid-2026-07-10.md — E1 PM ruling precedent, mirrored
// for Contacts (sprint-013 C3). The two-state "Active" (default) / "All"
// toggle in `ContactsView`'s header, and the two new empty-state copy
// strings it introduces — "Active" here means "not archived," the same
// meaning `archived_at == null` carries server-side.
// ---------------------------------------------------------------------------

export const CONTACTS_FILTER_TOGGLE_LABEL = "Show contacts";
export const CONTACTS_FILTER_ACTIVE_LABEL = "Active";
export const CONTACTS_FILTER_ALL_LABEL = "All";
export const CONTACTS_SHOW_ALL_LABEL = "Show all";

/** Mirrors `projectsListCountAnnouncement`/`animalsListCountAnnouncement` —
 * a live region announcing the rendered count, refreshed whenever the
 * toggle changes. */
export function contactsListCountAnnouncement(count: number): string {
  return `Contacts list, ${count} item${count === 1 ? "" : "s"}`;
}

/** Mirrors `noActiveProjectsHint` — the "zero active contacts, but archived
 * ones exist" empty state. The bracketed "[Show all]" text is a real
 * tappable control (rendered separately by the caller), never a dead-end
 * sentence. */
export function noActiveContactsHint(archivedCount: number): string {
  return `No active contacts right now. You have ${archivedCount} archived —`;
}

/** design-spec-animal-cards-2026-07-10.md §0 "Inactive-animal treatment,"
 * mirrored for a contact's archived state — same Option C rectangular tag
 * `PROJECT_ARCHIVED_TAG_LABEL` uses (the Admin list row family's own tag
 * shape, which `ContactsView` shares with `ProjectsView`). Never "Deleted"/
 * "Removed" — matches the archive-not-delete verb used everywhere else in
 * this app. */
export const CONTACT_ARCHIVED_TAG_LABEL = "Archived";

// ---------------------------------------------------------------------------
// design-spec-animal-cards-2026-07-10.md §5 — Archive (admin/api only,
// `ConfirmDialog` with `destructive`), mirrored for Contacts. Copy adapted
// from the Projects register above — same reassurance register (reversible,
// nothing deleted), reworded for a contact's own fields (phone/email/notes
// instead of status/checklist).
// ---------------------------------------------------------------------------

export function archiveContactConfirmTitle(contactName: string): string {
  return `Archive ${contactName}?`;
}

export const ARCHIVE_CONTACT_CONFIRM_BODY =
  "It'll be moved out of the default Contacts list. Their phone/email and notes stay on " +
  "record, and you can restore them any time — nothing is deleted.";

export const ARCHIVE_CONTACT_CONFIRM_LABEL = "Archive";
export const ARCHIVE_CONTACT_BUTTON_LABEL = "Archive";

export function contactArchivedMessage(contactName: string): string {
  return `${contactName} archived.`;
}

// ---------------------------------------------------------------------------
// sa-ruling-crud-endpoints-2026-07-10.md Decision 3.4 — Restore (admin/api
// only), mirroring Decision 2.4's Projects Restore. Restoring is
// reversible/non-destructive (the archive it undoes was itself already
// confirmed), so it deliberately does NOT go through `ConfirmDialog` —
// matching this app's own "no confirmation modals for reversible actions"
// rule (`ConfirmDialog.tsx`'s docstring).
// ---------------------------------------------------------------------------

export const RESTORE_CONTACT_BUTTON_LABEL = "Restore";

export function contactRestoredMessage(contactName: string): string {
  return `${contactName} restored.`;
}

// ---------------------------------------------------------------------------
// design-phase-c.md §0/§3.4/§6/§7 (RESOLVED R-C0b-1) — the record-level
// "Edited" provenance chip, shared by every surface that gains an
// edit-in-place affordance this phase (Overview notes, Care & health
// timeline items — Project detail is sprint-010 C4 scope, not built here).
// Plain text, no color, no icon; derived client-side, never a separate
// fetch. `updated_at`/`created_at` are ISO 8601 strings — a plain string
// compare is correct here (both always carry the same fixed-offset
// format the server emits) without needing a `Date` parse.
// ---------------------------------------------------------------------------
export const EDITED_CHIP_LABEL = "Edited";

export function isEditedRecord(createdAt: string, updatedAt: string): boolean {
  return updatedAt > createdAt;
}

// ---------------------------------------------------------------------------
// design-phase-c.md §2.3/§2.4/§3.1/§6 (RESOLVED R-C0b-1/R-C0b-2) — the
// edit-in-place affordances themselves (animal notes, health-event edit
// sheet, project edit sheet). Every PATCH here carries a required,
// client-generated `idempotency_key` (Decision 7.4).
// ---------------------------------------------------------------------------
export const EDIT_BUTTON_LABEL = "✎ Edit";
export const SAVE_LABEL = "Save";
export const SAVE_CHANGES_LABEL = "Save changes";
export const SAVED_MESSAGE = "Saved.";
export const COULD_NOT_SAVE_ERROR = "Couldn't save — try again.";
export const ADD_A_NOTE_LABEL = "+ Add a note";
export const EDIT_HEALTH_NOTE_ARIA_LABEL = "Edit this note";

export function editHealthNoteSheetTitle(animalName: string): string {
  return `Edit this note for ${animalName}`;
}

export function editProjectSheetTitle(projectName: string): string {
  return `Edit ${projectName}`;
}

/** §3.1's status chip picker — the real, complete `projectStatusLabel()`
 * set (RESOLVED R-C0b-4), offered as SUGGESTIONS a tap fills into the
 * Status field, never a closed/required enum — ADR-002 A6-R6 (SA ruling,
 * ratified) confirms `projects.status` stays free text with no server
 * enum, and explicitly recommends this exact "suggested values, free
 * text still accepted" shape over a hard-closed picker. */
export const PROJECT_STATUS_SUGGESTIONS: string[] = [
  "Open",
  "Getting quotes",
  "Waiting to start",
  "Working out the budget",
  "Underway",
  "Coming up",
  "Paused",
  "Wrapped up",
];

// ---------------------------------------------------------------------------
// design-phase-c.md §2.5/§6 — Weights tab. Pounds-only (US farm, no kg
// toggle, mirrors `AnimalWeightCreateIn.weight_lb`'s own doc). The
// sparkline's real, non-decorative caption text (§7 — the SVG itself is
// always `aria-hidden`).
// ---------------------------------------------------------------------------
export const WEIGHTS_EMPTY_HINT = "No weights logged yet.";
export const LOG_A_WEIGHT_LABEL = "+ Log a weight";
export const WEIGHT_REMOVE_CONFIRM_TITLE = "Remove this weight?";
export const WEIGHT_REMOVE_CONFIRM_BODY = "This can't be undone.";
export const WEIGHT_REMOVE_CONFIRM_LABEL = "Remove it";

/** `"142 lb"` — the weight row's own primary text and the sparkline
 * caption's number formatting. `weight_lb` is a float server-side
 * (`AnimalWeightOut.weight_lb: number`) but farm weights are always
 * whole pounds in practice; trims a trailing `.0` without rounding any
 * genuine fractional value away. */
export function weightLbLabel(weightLb: number): string {
  const rounded = Math.round(weightLb * 10) / 10;
  return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded} lb`;
}

/** §2.5/§6 — `"<latest> lb as of <date> — <up/down> <delta> lb since
 * <earliest-in-window date>."`, computed over the sparkline's own
 * (already-capped-to-10, newest-first) window. `null` when the window
 * has fewer than 2 entries — "no caption at all when only 1 entry exists
 * — nothing to compare" (§2.5). `rows` must be newest-first (this
 * function does not sort). */
export function weightTrendCaption(
  rowsNewestFirst: Array<{ weight_lb: number; weighed_on: string }>,
): string | null {
  if (rowsNewestFirst.length < 2) return null;
  const latest = rowsNewestFirst[0]!;
  const earliest = rowsNewestFirst[rowsNewestFirst.length - 1]!;
  const delta = Math.round((latest.weight_lb - earliest.weight_lb) * 10) / 10;
  const direction = delta >= 0 ? "up" : "down";
  const magnitude = Math.abs(delta);
  const magnitudeLabel = magnitude % 1 === 0 ? magnitude.toFixed(0) : String(magnitude);
  return (
    `${weightLbLabel(latest.weight_lb)} as of ${monthDayLabel(latest.weighed_on)} — ` +
    `${direction} ${magnitudeLabel} lb since ${monthDayLabel(earliest.weighed_on)}.`
  );
}

// ---------------------------------------------------------------------------
// design-phase-c.md §2.6/§6/§7/§10 — Photos tab. "Photo(s)," never
// "attachment(s)"/"media"/"asset(s)" (the zero-jargon sweep's own
// governed replacement). Every string below is checked against that
// sweep — no raw 413/415/EXIF/MinIO/S3/object-key vocabulary anywhere.
// ---------------------------------------------------------------------------
export const PHOTOS_EMPTY_HINT = "No photos yet.";
export const TAKE_A_PHOTO_LABEL = "Take a photo";
export const CHOOSE_A_PHOTO_LABEL = "Choose a photo";
export const UPLOAD_A_PHOTO_LABEL = "Upload a photo";
export const PHOTO_BAD_TYPE_ERROR = "That doesn't look like a photo — try a different file.";
export const PHOTO_TOO_BIG_ERROR = "That photo's too big — try a smaller one or a different photo.";
export const PHOTO_SERVER_REJECT_ERROR = "That photo couldn't be saved — try a different one.";
export const PHOTO_UPLOAD_FAILED_RETRY = "Couldn't upload — tap to try again.";
export const PHOTO_LIGHTBOX_CLOSE_LABEL = "Close";
/** The lightbox's destructive action. "Delete", never "Remove": it sat next
 * to "Remove as card photo" (a harmless pointer flip), and the shared verb
 * made a hard delete look like the same kind of action. */
export const PHOTO_DELETE_LABEL = "Delete photo";
export const PHOTO_REMOVE_CONFIRM_TITLE = "Delete this photo?";
export const PHOTO_REMOVE_CONFIRM_BODY =
  "It will be gone from this animal's record for everyone. This can't be undone.";
export const PHOTO_REMOVE_CONFIRM_LABEL = "Delete it";
export const PHOTO_DELETED_MESSAGE = "Photo deleted.";
export const PHOTO_DELETE_FAILED_MESSAGE = "Couldn't delete that photo — try again.";

/** §0/§7 — always the generated string, never `original_filename`
 * (display-only metadata, not trustworthy for any structural use, and
 * frequently a meaningless camera-roll name). */
export function photoAltText(animalName: string): string {
  return `Photo of ${animalName}`;
}

/** §7's grid-button accessible name: `"View photo of <animal>, <n> of
 * <total>"` — the inner `<img>` stays `alt=""` (decorative; this label
 * already carries the content, avoiding double-announcement).
 *
 * Sprint-013 B2 — `isCardPhoto` appends ", current card photo" so the
 * grid's visible `.photo-tile-primary-badge` (itself `aria-hidden`, to
 * avoid double-announcing) is represented in the accessible name too —
 * the same "never conveyed by a visual-only badge alone" rule design-
 * spec-animal-cards-2026-07-10.md §0 already applies to the card grid's
 * own chips. Defaults to `false` so every pre-existing call site's output
 * is unchanged. */
export function photoGridButtonLabel(
  animalName: string,
  index: number,
  total: number,
  isCardPhoto = false,
): string {
  const base = `View photo of ${animalName}, ${index} of ${total}`;
  return isCardPhoto ? `${base}, current card photo` : base;
}

// ---------------------------------------------------------------------------
// Sprint-013 B2 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
// Decision 4.4) — the Photos tab's "Set as card photo"/"Remove as card
// photo" affordance (admin/api only) and its current-photo indicator
// (visible to every role, matching the "Edited" chip's own read-visible-
// to-everyone posture). Deliberately distinct strings from "Delete photo"
// (`PHOTO_DELETE_LABEL` — hard-deletes the photo entirely) so the
// two very different actions can never be confused in the lightbox's
// shared action row.
// ---------------------------------------------------------------------------
export const SET_CARD_PHOTO_LABEL = "Set as card photo";
export const REMOVE_CARD_PHOTO_LABEL = "Remove as card photo";
export const CARD_PHOTO_BADGE_LABEL = "Card photo";

// 15 MB client-side pre-check cap (§2.6 — UX-only, never the security
// boundary; the server's own Pillow-decode + size-guarded read is the
// sole authoritative gate).
export const PHOTO_CLIENT_MAX_BYTES = 15 * 1024 * 1024;

// ---------------------------------------------------------------------------
// design-qa-sprint-005 F1 — `project.status` plain-language map.
//
// **Not a bound Postgres enum.** Unlike `HealthEventType`/`BidStatus`,
// `projects.status` is free text (`String(100)`, nullable — see
// `app/models/projects.py`), sourced from whatever a project's own Apple
// Notes said under "**Status:**" at import time (`app/importer/parsers.py`
// / `tests/test_farm_parsers.py`). There is no fixed enum to exhaustively
// map against — a future re-import could introduce a status string never
// seen before. Confirmed via the live farm DB (2026-07-07): the four
// values in production today are "Open", "Bidding", "Open backlog", and
// "In progress (budgeting)". Matching is trimmed + case-insensitive so
// "bidding"/"Bidding"/"BIDDING" all resolve the same way.
//
// "Bidding" is the one confirmed bid-jargon leak this map exists to close
// (screenshot 04, design-qa-sprint-005 F1). The other three confirmed
// values already read plainly in the admin's own words, so they're kept
// close to source rather than reworded for its own sake — "Open" stays
// "Open".
// ---------------------------------------------------------------------------
const PROJECT_STATUS_MAP: Record<string, string> = {
  open: "Open",
  bidding: "Getting quotes",
  "open backlog": "Waiting to start",
  "in progress (budgeting)": "Working out the budget",
  // Not seen in production data today, but plausible future/import
  // variants — mapped now so they don't reopen this leak later.
  "in progress": "Underway",
  in_progress: "Underway",
  planned: "Coming up",
  "on hold": "Paused",
  on_hold: "Paused",
  done: "Wrapped up",
  complete: "Wrapped up",
  completed: "Wrapped up",
  finished: "Wrapped up",
};

/** `project.status` -> plain label, or `null` for an absent status (the
 * field is nullable). An unmapped value (a status string not seen in the
 * confirmed production set or the plausible-variants list above) never
 * renders raw — it falls back to a light humanization pass (underscores/
 * hyphens to spaces, sentence case) rather than the literal server string
 * or a crash. Any status this fallback path actually renders in
 * production should be promoted into the map above with real wording —
 * flag for UI Designer/§4 dictionary update. */
export function projectStatusLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  const key = status.trim().toLowerCase();
  if (!key) return null;
  const mapped = PROJECT_STATUS_MAP[key];
  if (mapped) return mapped;
  const humanized = key.replace(/[_-]+/g, " ").trim();
  return humanized.charAt(0).toUpperCase() + humanized.slice(1);
}

// ---------------------------------------------------------------------------
// design-qa-sprint-005 F2 — `bid.status` (rendered under the "Quotes"
// heading, never called "bid" in UI-facing copy) plain-language map.
//
// **This one IS a bound Postgres enum** — `BidStatus` (`app/models/
// enums.py`) has exactly three members: `pending`, `accepted`, `declined`.
// No "received" state exists at the DB layer (the design-qa doc's
// suggested `received -> "Quote in"` row does not apply — flagging for
// design, see sprint report). All three are mapped below; an unrecognized
// value (only possible if the enum itself grows before this map is
// updated) falls back to the same safe humanization as
// `projectStatusLabel` rather than the raw value or a crash.
// ---------------------------------------------------------------------------
const QUOTE_STATUS_MAP: Record<string, string> = {
  pending: "Waiting to hear back",
  accepted: "Going with this one",
  declined: "Passed",
};

/** `bid.status` -> plain label for a Quotes row. */
export function quoteStatusLabel(status: string): string {
  const key = status.trim().toLowerCase();
  const mapped = QUOTE_STATUS_MAP[key];
  if (mapped) return mapped;
  const humanized = key.replace(/[_-]+/g, " ").trim();
  return humanized.charAt(0).toUpperCase() + humanized.slice(1);
}
